import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  playerswaptable,
  rotatePlayers,
  playeradtable,
  copyTableSetup,
} from "../components/funtionsBD/Mainservice";
import { fetchSessionTablesDeduped } from "../utils/sessionTableFetch";
import { seatedPlayerIds } from "../utils/tableSeats";
import {
  exercisesForStation,
  hasMultipleLiveStations,
  pickCoachStationControlIndex,
} from "../utils/liveTrainingStations";
import {
  applyOptimisticRankingAssign,
  applyOptimisticTableRotation,
  filterPresentRoster,
  filterRosterForStation,
  filterTablesForStation,
  findStationTable,
  formatCoachTableRow,
  getAssignedPlayerIds,
  normalizeSessionTables,
  normalizeSessionTablesForCoachSetup,
  getUnassignedPresentPlayers,
  planPresenceSeatAssignments,
  followingExercisesForTableCopy,
  resolveStationTables,
  sortRosterByRanking,
} from "../utils/coachSessionTables";

/** Slot key for swap UI state (table number + player1|player2). */
export function coachTableSlotKey(tableNumber, slot) {
  return `${tableNumber}:${slot}`;
}

function cloneRawTables(tables) {
  return (tables || []).map((t) => ({
    ...t,
    player1: t.player1 ? { ...t.player1 } : t.player1,
    player2: t.player2 ? { ...t.player2 } : t.player2,
    player3: t.player3 ? { ...t.player3 } : t.player3,
  }));
}

function applySwapToRawTables(tables, fromTableId, fromSlot, toTableId, toSlot) {
  const next = cloneRawTables(tables);
  const fromT = next.find((t) => String(t.table_id) === String(fromTableId));
  const toT = next.find((t) => String(t.table_id) === String(toTableId));
  if (!fromT) return tables;

  const fromPlayer = fromT[fromSlot] ?? null;
  const toPlayer = toT ? toT[toSlot] ?? null : null;

  fromT[fromSlot] =
    toPlayer && typeof toPlayer === "object" && Object.keys(toPlayer).length
      ? toPlayer
      : null;
  if (toT) {
    toT[toSlot] = fromPlayer;
  }
  return next;
}

const SWAP_CONFIRM_TIMEOUT_MS = 5000;

export function useCoachSessionTables({
  grId,
  tsId,
  teId,
  tsgId = null,
  /** Session stations/subgroups — used to resolve table→station when rows omit tsg_id. */
  stations = null,
  roster = [],
  exercise = null,
  exercises = [],
  socket,
  viewRefreshToken = 0,
  enabled = true,
}) {
  const [apiTables, setApiTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotatePending, setRotatePending] = useState(false);
  const [swapOpsById, setSwapOpsById] = useState({});
  /** Present players seated on another station's current exercise. */
  const [otherStationPresentPlayers, setOtherStationPresentPlayers] = useState(
    []
  );
  const swapTimeoutsRef = useRef({});
  const fetchScopeRef = useRef({ tsId: null, teId: null });
  const prevTsIdRef = useRef(tsId);
  const otherStationLoadGenRef = useRef(0);

  const clearSwapTimeouts = useCallback(() => {
    Object.values(swapTimeoutsRef.current).forEach((timerId) => {
      clearTimeout(timerId);
    });
    swapTimeoutsRef.current = {};
  }, []);

  const clearSwapOps = useCallback(() => {
    clearSwapTimeouts();
    setSwapOpsById({});
  }, [clearSwapTimeouts]);

  useEffect(() => () => clearSwapTimeouts(), [clearSwapTimeouts]);

  useEffect(() => {
    fetchScopeRef.current = {
      tsId: tsId != null ? String(tsId) : null,
      teId: teId != null ? String(teId) : null,
    };
    clearSwapOps();
    if (String(prevTsIdRef.current ?? "") !== String(tsId ?? "")) {
      setApiTables([]);
      prevTsIdRef.current = tsId;
    }
  }, [tsId, teId, clearSwapOps]);

  const reload = useCallback(async () => {
    if (!enabled || !grId || !tsId || !teId) {
      setApiTables([]);
      return [];
    }
    const scope = { tsId: String(tsId), teId: String(teId) };
    setLoading(true);
    setError(null);
    try {
      const list = await fetchSessionTablesDeduped({
        gr_id: grId,
        ts_id: tsId,
        te_id: teId,
      });
      const current = fetchScopeRef.current;
      if (
        String(current.tsId ?? "") !== scope.tsId ||
        String(current.teId ?? "") !== scope.teId
      ) {
        return [];
      }
      setApiTables(list);
      return list;
    } catch (e) {
      setError(e?.message || "Failed to load tables");
      setApiTables([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, grId, tsId, teId]);

  useEffect(() => {
    reload();
  }, [reload, viewRefreshToken]);

  useEffect(() => {
    if (!socket || !tsId || !enabled) return undefined;

    const onChanged = (data) => {
      if (!data?.ts_id || String(data.ts_id) !== String(tsId)) return;
      if (!data?.te_id || !teId || String(data.te_id) !== String(teId)) return;
      clearSwapOps();
      if (Array.isArray(data.tables)) {
        setApiTables(normalizeSessionTables(data.tables));
      } else {
        reload();
      }
      socket.emit("requestHrSnapshot", { ts_id: tsId, te_id: teId });
    };

    socket.on("trainingTableSetupChanged", onChanged);
    socket.on("playerTableChanged", onChanged);
    return () => {
      socket.off("trainingTableSetupChanged", onChanged);
      socket.off("playerTableChanged", onChanged);
    };
  }, [socket, tsId, teId, enabled, reload, clearSwapOps]);

  const getSlotSwapState = useCallback(
    (tableNumber, slot) => {
      const key = coachTableSlotKey(tableNumber, slot);
      for (const op of Object.values(swapOpsById)) {
        if (op.slotKeys?.includes(key)) {
          return {
            status: op.status,
            opId: op.opId,
            error: op.error,
          };
        }
      }
      return null;
    },
    [swapOpsById]
  );

  const isSlotSwapPending = useCallback(
    (tableNumber, slot) => getSlotSwapState(tableNumber, slot)?.status === "pending",
    [getSlotSwapState]
  );

  const clearSwapOp = useCallback((opId) => {
    if (swapTimeoutsRef.current[opId]) {
      clearTimeout(swapTimeoutsRef.current[opId]);
      delete swapTimeoutsRef.current[opId];
    }
    setSwapOpsById((prev) => {
      if (!prev[opId]) return prev;
      const next = { ...prev };
      delete next[opId];
      return next;
    });
  }, []);

  const executeSwapRequest = useCallback(
    async (opId, request, rollbackTables) => {
      const { fromTableId, fromSlot, toTableId, toSlot, fromPlayerId, toPlayerId } =
        request;
      if (!fromPlayerId) {
        setApiTables(rollbackTables);
        clearSwapOp(opId);
        return;
      }

      try {
        await playerswaptable({
          ex_table_id: fromTableId,
          ex_player_id: fromPlayerId,
          ex_player_position:
            fromSlot === "player1"
              ? "Player1"
              : fromSlot === "player3"
                ? "Player3"
                : "Player2",
          ex_ts_id: tsId,
          ex_te_id: teId,
          rep_table_id: toTableId,
          rep_player_id: toPlayerId || null,
          rep_player_position:
            toSlot === "player1"
              ? "Player1"
              : toSlot === "player3"
                ? "Player3"
                : "Player2",
          rep_ts_id: tsId,
          rep_te_id: teId,
        });
        clearSwapOp(opId);
        reload();
      } catch (e) {
        if (swapTimeoutsRef.current[opId]) {
          clearTimeout(swapTimeoutsRef.current[opId]);
          delete swapTimeoutsRef.current[opId];
        }
        setApiTables(rollbackTables);
        setSwapOpsById((prev) => {
          const op = prev[opId];
          if (!op) return prev;
          return {
            ...prev,
            [opId]: {
              ...op,
              status: "error",
              error: e?.message || "Failed to swap players",
            },
          };
        });
      }
    },
    [tsId, teId, reload, clearSwapOp]
  );

  const queueSwapPlayers = useCallback(
    (fromTable, fromSlot, toTable, toSlot) => {
      if (!tsId || !teId) return null;

      const slotKeys = [
        coachTableSlotKey(fromTable.tableNumber, fromSlot),
        coachTableSlotKey(toTable.tableNumber, toSlot),
      ];

      const slotBusy = slotKeys.some((key) => {
        for (const op of Object.values(swapOpsById)) {
          if (!op.slotKeys?.includes(key)) continue;
          if (op.status === "pending") return true;
        }
        return false;
      });
      if (slotBusy) return null;

      const fromPlayer = fromTable.raw?.[fromSlot];
      const toPlayer = toTable.raw?.[toSlot];
      if (!fromPlayer?.player_id) return null;

      const opId = `swap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const request = {
        fromTable,
        fromSlot,
        toTable,
        toSlot,
        fromTableId: fromTable.tableId,
        toTableId: toTable.tableId,
        fromPlayerId: fromPlayer.player_id,
        toPlayerId: toPlayer?.player_id || null,
      };
      let rollbackTables = null;

      setApiTables((prev) => {
        rollbackTables = cloneRawTables(prev);
        return applySwapToRawTables(
          prev,
          fromTable.tableId,
          fromSlot,
          toTable.tableId,
          toSlot
        );
      });

      setSwapOpsById((prev) => ({
        ...prev,
        [opId]: {
          opId,
          status: "pending",
          slotKeys,
          request,
          rollbackTables,
        },
      }));

      swapTimeoutsRef.current[opId] = setTimeout(() => {
        setSwapOpsById((prev) => {
          const op = prev[opId];
          if (!op || op.status !== "pending") return prev;
          return {
            ...prev,
            [opId]: {
              ...op,
              status: "timeout",
              error: "liveTrainingTableSwapTimeout",
            },
          };
        });
      }, SWAP_CONFIRM_TIMEOUT_MS);

      executeSwapRequest(opId, request, rollbackTables);
      return opId;
    },
    [tsId, teId, swapOpsById, executeSwapRequest]
  );

  const retrySwap = useCallback(
    (opId) => {
      const op = swapOpsById[opId];
      if (!op || (op.status !== "timeout" && op.status !== "error")) return;

      const { request, rollbackTables } = op;

      setApiTables((prev) =>
        applySwapToRawTables(
          prev,
          request.fromTable.tableId,
          request.fromSlot,
          request.toTable.tableId,
          request.toSlot
        )
      );

      setSwapOpsById((prev) => ({
        ...prev,
        [opId]: {
          ...prev[opId],
          status: "pending",
          error: null,
        },
      }));

      if (swapTimeoutsRef.current[opId]) {
        clearTimeout(swapTimeoutsRef.current[opId]);
      }
      swapTimeoutsRef.current[opId] = setTimeout(() => {
        setSwapOpsById((prev) => {
          const current = prev[opId];
          if (!current || current.status !== "pending") return prev;
          return {
            ...prev,
            [opId]: {
              ...current,
              status: "timeout",
              error: "liveTrainingTableSwapTimeout",
            },
          };
        });
      }, SWAP_CONFIRM_TIMEOUT_MS);

      executeSwapRequest(opId, request, rollbackTables);
    },
    [swapOpsById, executeSwapRequest]
  );

  const swapPlayers = useCallback(
    async (fromTable, fromSlot, toTable, toSlot) => {
      const opId = queueSwapPlayers(fromTable, fromSlot, toTable, toSlot);
      return Boolean(opId);
    },
    [queueSwapPlayers]
  );

  const rawTables = useMemo(
    () => normalizeSessionTablesForCoachSetup(apiTables),
    [apiTables]
  );

  const stationRawTables = useMemo(
    () => resolveStationTables(rawTables, tsgId, stations),
    [rawTables, tsgId, stations]
  );

  const stationPresentRoster = useMemo(
    () => filterRosterForStation(roster, rawTables, tsgId, stations),
    [roster, rawTables, tsgId, stations]
  );

  const tables = useMemo(
    () =>
      stationRawTables.map((t) => formatCoachTableRow(t, roster, exercise, null)),
    [stationRawTables, roster, exercise]
  );

  const assignedIds = useMemo(
    () => getAssignedPlayerIds(stationRawTables),
    [stationRawTables]
  );

  const resolveStationTe = useCallback(
    (stationId) => {
      const id = stationId != null ? String(stationId) : null;
      if (!id) return null;
      const stationExercises = exercisesForStation(exercises, id);
      const idx = pickCoachStationControlIndex(exercises, {}, id);
      if (idx >= 0 && exercises[idx]?.te_id) return exercises[idx];
      return stationExercises[0] || null;
    },
    [exercises]
  );

  const reloadOtherStationPlayers = useCallback(async () => {
    const gen = ++otherStationLoadGenRef.current;
    if (
      !enabled ||
      !grId ||
      !tsId ||
      !tsgId ||
      !hasMultipleLiveStations(stations)
    ) {
      setOtherStationPresentPlayers([]);
      return [];
    }

    const presentById = new Map();
    for (const p of filterPresentRoster(roster)) {
      const pid = p?.player_id ?? p?.playerId ?? p?.id;
      if (pid != null && pid !== "") presentById.set(String(pid), p);
    }
    if (!presentById.size) {
      setOtherStationPresentPlayers([]);
      return [];
    }

    const focused = String(tsgId);
    const others = (stations || []).filter(
      (s) => s?.tsg_id != null && String(s.tsg_id) !== focused
    );
    /** @type {Map<string, object>} */
    const seated = new Map();

    const slotOfPlayer = (table, pid) => {
      for (const key of ["player1", "player2", "player3"]) {
        const slot = table?.[key];
        const sid = slot?.player_id ?? slot?.playerId;
        if (sid != null && String(sid) === pid) return key;
      }
      const extras = table?.extras || [];
      for (let i = 0; i < extras.length; i++) {
        const sid = extras[i]?.player_id ?? extras[i]?.playerId;
        if (sid != null && String(sid) === pid) return `extra:${i}`;
      }
      return "player1";
    };

    await Promise.all(
      others.map(async (station) => {
        const stationId = String(station.tsg_id);
        const stationExercises = exercisesForStation(exercises, stationId);
        // Prefer control TE first, then scan remaining so we find seats even
        // when seating lives on a non-focused block of that station.
        const ordered = [];
        const control = resolveStationTe(stationId);
        if (control?.te_id) ordered.push(control);
        for (const ex of stationExercises) {
          if (
            ex?.te_id &&
            !ordered.some((e) => String(e.te_id) === String(ex.te_id))
          ) {
            ordered.push(ex);
          }
        }
        for (const teOther of ordered) {
          try {
            const tables = await fetchSessionTablesDeduped({
              gr_id: grId,
              ts_id: tsId,
              te_id: teOther.te_id,
            });
            const stationTables = filterTablesForStation(
              tables,
              stationId,
              stations
            );
            for (const table of stationTables) {
              for (const pid of seatedPlayerIds(table)) {
                if (seated.has(pid)) continue;
                seated.set(pid, {
                  seated_tsg_id: stationId,
                  seated_te_id: teOther.te_id,
                  seated_table_id: table.table_id ?? table.tableId,
                  seated_table_number:
                    table.table_number ?? table.tableNumber ?? null,
                  seated_slot: slotOfPlayer(table, pid),
                });
              }
            }
          } catch {
            /* ignore station fetch errors */
          }
        }
      })
    );

    if (gen !== otherStationLoadGenRef.current) return [];

    const next = [];
    for (const [pid, meta] of seated) {
      const row = presentById.get(pid);
      if (!row) continue;
      next.push({ ...row, ...meta });
    }
    next.sort((a, b) =>
      String(a.user_last_name || a.user_alias || "").localeCompare(
        String(b.user_last_name || b.user_alias || "")
      )
    );
    setOtherStationPresentPlayers(next);
    return next;
  }, [
    enabled,
    grId,
    tsId,
    tsgId,
    stations,
    exercises,
    roster,
    resolveStationTe,
  ]);

  useEffect(() => {
    void reloadOtherStationPlayers();
  }, [reloadOtherStationPlayers, viewRefreshToken, apiTables]);

  const rotate = useCallback(
    async (direction) => {
      if (!tsId || !teId || rotatePending) return false;
      setError(null);

      let rollbackTables = null;
      setApiTables((prev) => {
        rollbackTables = cloneRawTables(prev);
        const station = filterTablesForStation(prev, tsgId, stations);
        if (tsgId && station.length > 0 && station.length < prev.length) {
          const rotatedStation = applyOptimisticTableRotation(
            station,
            roster,
            direction
          );
          const byId = new Map(
            rotatedStation.map((t) => [String(t.table_id), t])
          );
          return prev.map((t) => byId.get(String(t.table_id)) || t);
        }
        return applyOptimisticTableRotation(prev, roster, direction);
      });
      setRotatePending(true);

      try {
        const res = await rotatePlayers({ ts_id: tsId, te_id: teId, direction });
        const serverTables = res?.data?.data?.tables;
        if (Array.isArray(serverTables) && serverTables.length > 0) {
          setApiTables(normalizeSessionTables(serverTables));
        }
        return true;
      } catch (e) {
        if (rollbackTables) {
          setApiTables(rollbackTables);
        }
        setError(e?.message || "Failed to rotate players");
        return false;
      } finally {
        setRotatePending(false);
      }
    },
    [tsId, teId, rotatePending, roster, tsgId, stations]
  );

  const assignByRanking = useCallback(
    async (ascending = false, rosterOverride = null) => {
      const rosterToUse = rosterOverride
        ? filterPresentRoster(rosterOverride).length
          ? filterPresentRoster(rosterOverride)
          : rosterOverride
        : stationPresentRoster;
      if (!tsId || !teId || busy || !rosterToUse.length) return false;
      const physical = filterTablesForStation(
        rawTables.filter((t) => t.table_id && t.table_number),
        tsgId,
        stations
      ).slice(0, Math.ceil(rosterToUse.length / 2));
      if (physical.length === 0) {
        setError("No physical tables configured for this session.");
        return false;
      }

      const sortedPlayers = sortRosterByRanking(rosterToUse, ascending);
      const maxPlayers = physical.length * 2;
      const playersToAssign = sortedPlayers.slice(0, maxPlayers);

      let rollbackTables = null;
      setApiTables((prev) => {
        rollbackTables = cloneRawTables(prev);
        return applyOptimisticRankingAssign(
          prev,
          rosterToUse,
          ascending,
          tsgId,
          stations
        );
      });

      setBusy(true);
      setError(null);
      try {
        for (let i = 0; i < playersToAssign.length; i += 2) {
          const table = physical[Math.floor(i / 2)];
          if (!table) break;
          const player1 = playersToAssign[i];
          const player2 = playersToAssign[i + 1];
          if (!player1) continue;

          const params = {
            player1: player1.player_id || player1.id,
            table_id: table.table_id,
            ts_id: tsId,
            te_id: teId,
          };
          if (player2) {
            params.player2 = player2.player_id || player2.id;
          }

          await playeradtable(params);
        }
        await reload();
        return true;
      } catch (e) {
        if (rollbackTables) {
          setApiTables(rollbackTables);
        }
        setError(e?.message || "Failed to assign players");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [tsId, teId, busy, stationPresentRoster, rawTables, reload, tsgId, stations]
  );

  /**
   * Seat players after Manage Presence:
   * empty/1-player tables by number, then waiting-pool round-robin.
   * Returns { ok, reason?, unassignedCount?, seatedPlans? }.
   */
  const assignFromPresence = useCallback(
    async (presentPlayers = []) => {
      if (!tsId || !teId) return { ok: false, reason: "missing_ids" };
      if (busy) return { ok: false, reason: "busy" };
      if (!Array.isArray(presentPlayers) || presentPlayers.length === 0) {
        return { ok: false, reason: "empty" };
      }

      setBusy(true);
      setError(null);
      try {
        const freshTables = await reload();
        const tablesForPlan =
          Array.isArray(freshTables) && freshTables.length
            ? freshTables
            : rawTables;
        const scopedPresent = filterRosterForStation(
          presentPlayers,
          tablesForPlan,
          tsgId,
          stations
        );
        const plans = planPresenceSeatAssignments(
          scopedPresent,
          tablesForPlan,
          tsgId,
          stations
        );
        const copyToFollowing = async () => {
          const following = followingExercisesForTableCopy(exercises, teId);
          for (const ex of following) {
            try {
              await copyTableSetup({
                from_te_id: teId,
                to_te_id: ex.te_id,
                ts_id: tsId,
              });
            } catch (copyErr) {
              console.error(
                "useCoachSessionTables - copy table setup to following failed:",
                copyErr
              );
            }
          }
        };
        if (plans.length === 0) {
          const unassigned = getUnassignedPresentPlayers(
            scopedPresent,
            filterTablesForStation(tablesForPlan, tsgId, stations)
          );
          if (unassigned.length > 0) {
            const msg =
              "Could not seat newly present players. Check tables or open table setup.";
            setError(msg);
            return {
              ok: false,
              reason: "no_seats",
              unassignedCount: unassigned.length,
            };
          }
          await copyToFollowing();
          return { ok: true, reason: "already_seated", seatedPlans: 0 };
        }

        for (const plan of plans) {
          const params = {
            table_id: plan.table_id,
            ts_id: tsId,
            te_id: teId,
          };
          if (Array.isArray(plan.player_ids) && plan.player_ids.length > 0) {
            params.player_ids = plan.player_ids;
          } else {
            params.player1 = plan.player1;
            if (plan.player2) params.player2 = plan.player2;
          }
          await playeradtable(params);
        }
        await reload();
        await copyToFollowing();
        return { ok: true, seatedPlans: plans.length };
      } catch (e) {
        const msg = e?.message || "Failed to assign players from presence";
        setError(msg);
        return { ok: false, reason: "error", message: msg };
      } finally {
        setBusy(false);
      }
    },
    [tsId, teId, busy, rawTables, reload, exercises, tsgId, stations]
  );

  /**
   * Force-seat one or more present players onto a table in a single assign.
   * Reloads fresh seats first so multi-select moves do not drop earlier movers.
   * `tableRef` may be a table number or `{ tableNumber, tableId }`.
   */
  const movePlayersForce = useCallback(
    async (playerIds, tableRef) => {
      const movers = [
        ...new Set(
          (Array.isArray(playerIds) ? playerIds : [playerIds])
            .map((id) => (id != null && id !== "" ? String(id) : null))
            .filter(Boolean)
        ),
      ];
      const tableId =
        tableRef != null && typeof tableRef === "object"
          ? tableRef.tableId ?? tableRef.table_id ?? null
          : null;
      const tn = Number(
        tableRef != null && typeof tableRef === "object"
          ? tableRef.tableNumber ?? tableRef.table_number
          : tableRef
      );
      if (
        !tsId ||
        !teId ||
        !movers.length ||
        ((!Number.isFinite(tn) || tn <= 0) && (tableId == null || tableId === ""))
      ) {
        return { ok: false, reason: "bad_args" };
      }
      if (busy) return { ok: false, reason: "busy" };

      setBusy(true);
      setError(null);
      try {
        const fresh = await reload();
        const source =
          Array.isArray(fresh) && fresh.length > 0
            ? normalizeSessionTablesForCoachSetup(fresh)
            : rawTables;
        // Same scope the UI uses (station tables, or untagged fallback).
        const stationTables = resolveStationTables(source, tsgId, stations);
        let target = findStationTable(stationTables, {
          tableId,
          tableNumber: tn,
        });
        // Last resort: match by id/number in the full room list so a visible
        // card never fails with "table not found".
        if (!target) {
          target = findStationTable(source, { tableId, tableNumber: tn });
        }
        if (!target?.table_id && !target?.tableId) {
          setError(
            `Table ${Number.isFinite(tn) && tn > 0 ? tn : tableId || "?"} not found`
          );
          return { ok: false, reason: "table_not_found" };
        }
        const targetTableId = target.table_id ?? target.tableId;

        const seatedIds = seatedPlayerIds(target);
        const moverSet = new Set(movers);
        const kept = seatedIds.filter((id) => !moverSet.has(id));
        const nextIds = [...kept];
        for (const pid of movers) {
          if (!nextIds.includes(pid)) nextIds.push(pid);
        }

        await playeradtable({
          table_id: targetTableId,
          ts_id: tsId,
          te_id: teId,
          player_ids: nextIds,
        });
        await reload();
        await reloadOtherStationPlayers();
        return {
          ok: true,
          tableNumber: Number(target.table_number ?? target.tableNumber) || tn,
          playerIds: movers,
        };
      } catch (e) {
        const msg = e?.message || "Failed to move player";
        setError(msg);
        return { ok: false, reason: "error", message: msg };
      } finally {
        setBusy(false);
      }
    },
    [
      tsId,
      teId,
      busy,
      rawTables,
      reload,
      reloadOtherStationPlayers,
      tsgId,
      stations,
    ]
  );

  const movePlayerForce = useCallback(
    (playerId, tableNumber, tableId = null) =>
      movePlayersForce(
        [playerId],
        tableId != null ? { tableNumber, tableId } : tableNumber
      ),
    [movePlayersForce]
  );

  /**
   * Move present players onto a table on another (or current) station.
   * Uses that station's control exercise te_id for the assign.
   */
  const movePlayersToStation = useCallback(
    async (playerIds, destTsgId, tableNumber = null) => {
      const movers = [
        ...new Set(
          (Array.isArray(playerIds) ? playerIds : [playerIds])
            .map((id) => (id != null && id !== "" ? String(id) : null))
            .filter(Boolean)
        ),
      ];
      const destId = destTsgId != null ? String(destTsgId) : null;
      if (!tsId || !grId || !movers.length || !destId) {
        return { ok: false, reason: "bad_args" };
      }
      if (busy) return { ok: false, reason: "busy" };

      // Same station → reuse current-te move helper when possible.
      if (tsgId && String(tsgId) === destId && teId) {
        const tn = Number(
          tableNumber != null && typeof tableNumber === "object"
            ? tableNumber.tableNumber ?? tableNumber.table_number
            : tableNumber
        );
        const tableId =
          tableNumber != null && typeof tableNumber === "object"
            ? tableNumber.tableId ?? tableNumber.table_id ?? null
            : null;
        if ((Number.isFinite(tn) && tn > 0) || tableId) {
          return movePlayersForce(
            movers,
            tableId != null ? { tableNumber: tn, tableId } : tn
          );
        }
      }

      const destTe = resolveStationTe(destId);
      if (!destTe?.te_id) {
        setError("No exercise on destination station");
        return { ok: false, reason: "no_dest_te" };
      }

      setBusy(true);
      setError(null);
      try {
        const fetched = await fetchSessionTablesDeduped({
          gr_id: grId,
          ts_id: tsId,
          te_id: destTe.te_id,
        });
        const tables = resolveStationTables(fetched, destId, stations);
        const tn = Number(
          tableNumber != null && typeof tableNumber === "object"
            ? tableNumber.tableNumber ?? tableNumber.table_number
            : tableNumber
        );
        const tableId =
          tableNumber != null && typeof tableNumber === "object"
            ? tableNumber.tableId ?? tableNumber.table_id ?? null
            : null;
        let target = findStationTable(tables, { tableId, tableNumber: tn });
        if (!target) {
          target = findStationTable(fetched, { tableId, tableNumber: tn });
        }
        if (!target) {
          const byNumber = [...tables].sort(
            (a, b) =>
              (Number(a.table_number) || 0) - (Number(b.table_number) || 0)
          );
          target =
            byNumber.find((t) => seatedPlayerIds(t).length < 2) ||
            byNumber[0] ||
            null;
        }
        const targetTableId = target?.table_id ?? target?.tableId;
        if (!targetTableId) {
          setError("No table on destination station");
          return { ok: false, reason: "table_not_found" };
        }

        const seatedIds = seatedPlayerIds(target);
        const moverSet = new Set(movers);
        const kept = seatedIds.filter((id) => !moverSet.has(id));
        const nextIds = [...kept];
        for (const pid of movers) {
          if (!nextIds.includes(pid)) nextIds.push(pid);
        }

        await playeradtable({
          table_id: targetTableId,
          ts_id: tsId,
          te_id: destTe.te_id,
          player_ids: nextIds,
        });
        await reload();
        await reloadOtherStationPlayers();
        return {
          ok: true,
          tableNumber: target.table_number ?? target.tableNumber,
          destTsgId: destId,
          destTeId: destTe.te_id,
          playerIds: movers,
        };
      } catch (e) {
        const msg = e?.message || "Failed to move player to station";
        setError(msg);
        return { ok: false, reason: "error", message: msg };
      } finally {
        setBusy(false);
      }
    },
    [
      busy,
      grId,
      movePlayersForce,
      reload,
      reloadOtherStationPlayers,
      resolveStationTe,
      stations,
      teId,
      tsId,
      tsgId,
    ]
  );

  /**
   * Swap two seated players across stations (or tables) using distinct te_ids.
   * Each side: { playerId, tableId, teId, slot } where slot is player1|player2|player3.
   */
  const swapAcrossStations = useCallback(
    async (sideA, sideB) => {
      const aPid = sideA?.playerId != null ? String(sideA.playerId) : "";
      const bPid = sideB?.playerId != null ? String(sideB.playerId) : "";
      if (
        !tsId ||
        !aPid ||
        !bPid ||
        !sideA?.tableId ||
        !sideB?.tableId ||
        !sideA?.teId ||
        !sideB?.teId
      ) {
        return { ok: false, reason: "bad_args" };
      }
      if (busy) return { ok: false, reason: "busy" };

      const toPosition = (slot) => {
        if (slot === "player1") return "Player1";
        if (slot === "player3") return "Player3";
        return "Player2";
      };

      setBusy(true);
      setError(null);
      try {
        await playerswaptable({
          ex_table_id: sideA.tableId,
          ex_player_id: aPid,
          ex_player_position: toPosition(sideA.slot),
          ex_ts_id: tsId,
          ex_te_id: sideA.teId,
          rep_table_id: sideB.tableId,
          rep_player_id: bPid,
          rep_player_position: toPosition(sideB.slot),
          rep_ts_id: tsId,
          rep_te_id: sideB.teId,
        });
        await reload();
        await reloadOtherStationPlayers();
        return { ok: true };
      } catch (e) {
        const msg = e?.message || "Failed to swap players across stations";
        setError(msg);
        return { ok: false, reason: "error", message: msg };
      } finally {
        setBusy(false);
      }
    },
    [busy, reload, reloadOtherStationPlayers, tsId]
  );

  const loadStationTables = useCallback(
    async (stationId) => {
      const destId = stationId != null ? String(stationId) : null;
      if (!grId || !tsId || !destId) return [];
      const destTe = resolveStationTe(destId);
      if (!destTe?.te_id) return [];
      try {
        const tables = resolveStationTables(
          await fetchSessionTablesDeduped({
            gr_id: grId,
            ts_id: tsId,
            te_id: destTe.te_id,
          }),
          destId,
          stations
        );
        return tables.map((t) => {
          const row = formatCoachTableRow(
            { ...t, tsg_id: t.tsg_id || destId },
            roster,
            destTe,
            null
          );
          return {
            ...row,
            teId: destTe.te_id,
            tsgId: row.tsgId || destId,
          };
        });
      } catch {
        return [];
      }
    },
    [grId, roster, resolveStationTe, stations, tsId]
  );

  return {
    tables,
    rawTables,
    stationRawTables,
    stationPresentRoster,
    otherStationPresentPlayers,
    loading,
    error,
    busy,
    rotatePending,
    assignedIds,
    reload,
    reloadOtherStationPlayers,
    swapPlayers,
    queueSwapPlayers,
    getSlotSwapState,
    isSlotSwapPending,
    retrySwap,
    rotate,
    assignByRanking,
    assignFromPresence,
    movePlayerForce,
    movePlayersForce,
    movePlayersToStation,
    swapAcrossStations,
    loadStationTables,
    resolveStationTe,
  };
}
